/** Renders an emit-events task result: outcome counts, the run's tag and links
 *  (full report, audit trail, the run's deliveries), the dry run's caveats, and a
 *  bounded per-record table. Each dry-run row expands to its candidate
 *  notifications — MATCH, or the reason it would not match. Used inline after a
 *  dry run and as the Background Tasks result panel.
 */
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LaunchIcon from '@mui/icons-material/Launch';
import {
  Alert,
  Box,
  Chip,
  Collapse,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Fragment, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { deliveryLogsHrefForTag } from '@/api/bookingEdit';
import type { EmitEventOutcome, EmitEventRecord, EmitEventTaskResult } from '@/api/emitEvents';
import { OUTCOME_LABELS, OUTCOME_SEVERITY } from '@/api/emitEvents';

import { contextSummary, nounFor } from './model';

const MAX_ROWS_SHOWN = 100;
const linkSx = { display: 'inline-flex', alignItems: 'center', gap: 0.25 };

function PreviewList({ record }: { record: EmitEventRecord }) {
  const preview = record.preview ?? [];
  if (preview.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        No notification is configured for this event on this tenant.
      </Typography>
    );
  }
  return (
    <Stack spacing={0.5} sx={{ py: 0.5 }}>
      {preview.map((p) => (
        <Stack key={p.notification_id} direction="row" spacing={1} alignItems="baseline">
          <Chip
            size="small"
            color={p.match ? 'success' : 'default'}
            variant={p.match ? 'filled' : 'outlined'}
            label={p.match ? 'MATCH' : 'REJECT'}
            sx={{ minWidth: 72 }}
          />
          <Typography variant="body2" component="span">
            <Link component={RouterLink} to={`/notifications/${p.notification_id}`} target="_blank" rel="noopener">
              [{p.notification_id}] {p.name}
            </Link>
            {p.methods.length > 0 && (
              <Typography variant="caption" color="text.secondary" component="span">
                {' '}
                · {p.methods.join(', ')}
              </Typography>
            )}
            {!p.match && p.reason && (
              <Typography variant="caption" color="text.secondary" component="div">
                {p.reason}
              </Typography>
            )}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

function matchSummary(rec: EmitEventRecord): string {
  if (!rec.preview) return rec.logid ? `logid ${rec.logid}` : '—';
  const matched = rec.preview.filter((p) => p.match).length;
  return `${matched} of ${rec.preview.length} match`;
}

export function EmitEventsReport({ result, taskId }: { result: EmitEventTaskResult; taskId: string }) {
  const [open, setOpen] = useState<Record<number, boolean>>({});
  const rows = result.records.slice(0, MAX_ROWS_SHOWN);
  const noun = nounFor(result.family);
  const recordPath = (pk: number) => (result.family === 'order' ? `/orders/${pk}` : `/bookings/${pk}`);

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        {Object.entries(result.counts).map(([code, n]) => (
          <Chip
            key={code}
            size="small"
            variant="outlined"
            color={OUTCOME_SEVERITY[code as EmitEventOutcome] ?? 'default'}
            label={`${OUTCOME_LABELS[code as EmitEventOutcome] ?? code}: ${(n ?? 0).toLocaleString()}`}
          />
        ))}
        <Box sx={{ flexGrow: 1 }} />
        <Link component={RouterLink} to={`/bgtasks/${taskId}`} variant="caption" sx={linkSx}>
          Full report <LaunchIcon sx={{ fontSize: 14 }} />
        </Link>
        <Link component={RouterLink} to={`/audit?task_id=${taskId}`} variant="caption" sx={linkSx}>
          Audit trail <LaunchIcon sx={{ fontSize: 14 }} />
        </Link>
        {!result.dry_run && (
          <Link component={RouterLink} to={deliveryLogsHrefForTag(result.tag)} variant="caption" sx={linkSx}>
            Deliveries <LaunchIcon sx={{ fontSize: 14 }} />
          </Link>
        )}
      </Stack>

      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
        <code>{result.event}</code>
        {result.typename !== result.event && (
          <>
            {' '}
            (built as <code>{result.typename}</code>)
          </>
        )}
        , tagged <code>{result.tag}</code>
        {result.webhooks ? ' · customer webhooks on' : ''}
        {result.force ? ' · gates forced' : ''}.
      </Typography>

      {result.dry_run && result.caveats.length > 0 && (
        <Alert severity="info" sx={{ mb: 1 }}>
          {result.caveats.map((c) => (
            <div key={c}>{c}</div>
          ))}
        </Alert>
      )}

      <Box sx={{ maxHeight: 360, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }} />
              <TableCell sx={{ width: 150 }}>{noun === 'order' ? 'Order' : 'Booking'}</TableCell>
              <TableCell sx={{ width: 90 }}>Tenant</TableCell>
              <TableCell sx={{ width: 200 }}>Outcome</TableCell>
              <TableCell>Context</TableCell>
              <TableCell sx={{ width: 150 }}>{result.dry_run ? 'Notifications' : 'Event'}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((rec) => {
              const expandable = rec.preview !== undefined;
              const isOpen = !!open[rec.pk];
              return (
                <Fragment key={rec.pk}>
                  <TableRow hover>
                    <TableCell padding="checkbox">
                      {expandable && (
                        <IconButton
                          size="small"
                          aria-label={isOpen ? 'Hide notifications' : 'Show notifications'}
                          onClick={() => setOpen((o) => ({ ...o, [rec.pk]: !isOpen }))}
                        >
                          {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      )}
                    </TableCell>
                    <TableCell>
                      <Link component={RouterLink} to={recordPath(rec.pk)} target="_blank" rel="noopener">
                        {rec.ref || `#${rec.pk}`}
                      </Link>
                      <Typography variant="caption" color="text.secondary" component="div">
                        #{rec.pk}
                      </Typography>
                    </TableCell>
                    <TableCell>{rec.tenant ?? '—'}</TableCell>
                    <TableCell>
                      <Tooltip title={rec.detail ?? ''}>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={OUTCOME_SEVERITY[rec.outcome] ?? 'default'}
                          label={OUTCOME_LABELS[rec.outcome] ?? rec.outcome}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 380 }} title={contextSummary(rec.context)}>
                        {contextSummary(rec.context)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{matchSummary(rec)}</Typography>
                    </TableCell>
                  </TableRow>
                  {expandable && (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ py: 0, borderBottom: isOpen ? undefined : 'none' }}>
                        <Collapse in={isOpen} unmountOnExit>
                          <PreviewList record={rec} />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </Box>
      {result.records.length > MAX_ROWS_SHOWN && (
        <Typography variant="caption" color="text.secondary">
          Showing the first {rows.length.toLocaleString()} of {result.total.toLocaleString()} {noun}(s) — the task
          log holds every line.
        </Typography>
      )}
    </Box>
  );
}
